import { useMutation, useQuery } from 'react-query';
import { useEffect, useState } from 'react';
import Big from 'big.js';

import { useRequestCreditCard } from 'pages/booking/payment/hooks/useRequestCreditCard';
import { Service, JsonDataItem, PaymentMethod, ServiceType } from 'types';
import { parseErrors, Toast } from 'helpers';
import {
  putJob,
  getPaymentInfo,
  getInvoicePaymentMethods,
  postServiceInvoice,
  getServiceInvoice,
  getService,
  getServiceReceipt,
  getCustomer,
  postEmailServiceInvoice,
  postProcessPayment,
  postRequestPayment,
} from 'queries';

export enum PaymentSteps {
  GenerateInvoice,
  ProcessPayment,
  Paid,
}

export const useServicePayment = (job: Service) => {
  const [jobDetails, setJobDetails] = useState<JsonDataItem | null>();
  const [step, setStep] = useState<PaymentSteps>(PaymentSteps.GenerateInvoice);
  const [hasCard, setHasCard] = useState<boolean>();
  const [pendingReceiptNotification, setPendingReceiptNotification] = useState<boolean>(false);
  const [incompleteDetailsMessage, setIncompleteDetailsMessage] = useState<string>();
  const [payingCustomer, setPayingCustomer] = useState<JsonDataItem>(job?.customer);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | undefined>(
    !job.payment_method ? undefined : (job.payment_method as PaymentMethod), // BE return "" if not specified
  );

  const getWarningMessage = (hasDescription: boolean, hasServices: boolean) => {
    if (hasDescription && hasServices) return;

    const missingItems = [!hasDescription && 'a service description', !hasServices && 'services and fees']
      .filter(Boolean)
      .join(' and ');

    return `To proceed, please add ${missingItems} on the Service Invoice Details tab.`;
  };

  // Available methods
  const { data: paymentMethods, isLoading: isPaymentMethodsLoading } = useQuery(
    ['invoice-payment-methods', job?.ID],
    getInvoicePaymentMethods,
    {
      enabled: !!job?.ID,
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    },
  );

  // Bill to 3rd party
  useQuery(['customer-data', job?.billto_ID], getCustomer, {
    enabled: job?.billto_ID !== job?.customer.ID,
    onSuccess: (data: JsonDataItem) => {
      setPayingCustomer(data);
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // check if cc is available
  const { data: paymentInfo, isLoading: isPaymentInfoLoading } = useQuery(
    ['payment-info', job?.billto_ID],
    getPaymentInfo,
    {
      enabled: !!job?.billto_ID,
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    },
  );
  useEffect(() => {
    const isCardValid = paymentInfo?.card_details.is_card_valid;
    setHasCard(isCardValid);
    isCardValid && setPaymentMethod(PaymentMethod.CreditCard);
  }, [paymentInfo]);

  // update job and set paying customer
  const { mutate: updateJob, isLoading: isServiceUpdating } = useMutation(putJob, {
    onSuccess: (data: Service) => {
      setJobDetails(data);
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });
  const updateThirdParty = (id: number) => {
    updateJob({
      serviceId: job?.ID as number,
      billtoId: id,
    });
  };
  useEffect(() => {
    job?.billto_ID === job?.customer.ID && setPayingCustomer(job?.customer);
  }, [job?.billto_ID]);

  // Invoices (get, create, send)
  const {
    data: invoices,
    isLoading: isInvoicesLoading,
    refetch: refetchInvoices,
  } = useQuery(['service-invoice', job?.ID], getServiceInvoice, {
    enabled: !!job?.ID,
    onSuccess: (data) => setPaymentMethod(data[data.length - 1].payment_method),
  });
  const {
    mutate: createInvoice,
    isSuccess: isInvoiceGenerated,
    isLoading: isInvoiceCreating,
  } = useMutation(postServiceInvoice, {
    onSuccess: () => {
      refetchInvoices();
      setStep(PaymentSteps.ProcessPayment);
      Toast.show({ description: 'Invoice has been generated.', type: Toast.Type.Success });
      Toast.show({ description: `Job ${job?.ID} has been closed.`, type: Toast.Type.Success });
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });
  const generateInvoice = () => {
    const isLegacy = job?.support_legacy;
    const isCancelled = job?.status === 'cancelled';
    const hasDescription = !!job?.attributes?.find((attribute: JsonDataItem) => attribute.key === 'technician_report')
      ?.value;
    const hasProducts = isLegacy ? Big(job?.job?.labour_hours_total || 0).gt(0) : !!job?.products?.length;
    const isPartsOnly = isLegacy ? job?.job?.job_type === 'S' : job?.service_type_ID === (4 as unknown as ServiceType);

    (hasDescription && (hasProducts || isPartsOnly)) || isCancelled
      ? createInvoice({ serviceId: job?.ID, paymentMethod: paymentMethod as PaymentMethod })
      : setIncompleteDetailsMessage(getWarningMessage(hasDescription, hasProducts));
  };
  // Update service when invoice generated (BE unable to send updated job)
  const { isLoading: isServiceLoading } = useQuery(['check-service-details', job?.ID.toString()], getService, {
    enabled: isInvoiceGenerated,
    onSuccess: (data) => {
      setJobDetails(data);
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });
  const { mutate: sendEmailWithInvoice, isLoading: sendingEmail } = useMutation(postEmailServiceInvoice, {
    onSuccess: () => {
      Toast.show({ description: 'Invoice has been sent to the customer.', type: Toast.Type.Success });
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });
  const sendEmail = () => sendEmailWithInvoice({ serviceId: job?.ID });

  // Request Payment
  const { initialValues: initialContactValues, emailOptions, smsOptions } = useRequestCreditCard(payingCustomer);
  const {
    mutate: requestPaymentCall,
    isLoading: isRequestSending,
    isSuccess: isPaymentRequested,
  } = useMutation(postRequestPayment, {
    onSuccess: () => {
      Toast.show({ description: 'Request has been sent.', type: Toast.Type.Success });
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });
  const requestPayment = (contactData: JsonDataItem) => {
    requestPaymentCall({
      jobId: job?.ID,
      sendEmail: !!contactData.emailNotification.length,
      sendSms: contactData.smsNotification.includes(job?.customer?.contact1.contact1_mobile),
      secondaryNumber: contactData.smsNotification.filter(
        (phone: string) => phone === job?.customer?.contact2.contact2_mobile,
      )?.[0],
    });
  };

  // Payment Processing and Receipt
  const {
    data: receipts,
    isLoading: isReceiptsLoading,
    refetch: updateReceipt,
  } = useQuery(['service-receipt', job?.ID], getServiceReceipt, {
    enabled: !!job?.ID && job?.status === 'completed',
    onSuccess: (data) => {
      if (pendingReceiptNotification) {
        Toast.show({
          description: `Receipt #${data?.[data.length - 1]?.ID} has been sent to the customer.`,
          type: Toast.Type.Success,
        });
        setPendingReceiptNotification(false);
      }
    },
  });
  const { mutate: payJob, isLoading: isPaymentProcessing } = useMutation(postProcessPayment, {
    onSuccess: (_, variables) => {
      if (variables.paymentMethod === PaymentMethod.CreditCard) {
        Toast.show({ description: 'Payment successful.', type: Toast.Type.Success });
      }
      Toast.show({
        description: `Invoice #${invoices?.[invoices.length - 1]?.ID} has been sent to the customer.`,
        type: Toast.Type.Success,
      });
      setPendingReceiptNotification(true);
      updateReceipt();
      setStep(PaymentSteps.Paid);
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });
  const processPayment = () => payJob({ serviceId: job?.ID, paymentMethod: paymentMethod as PaymentMethod });

  const invoiceSubtotal = invoices?.length ? invoices.reduce((sum, invoice) => sum + Number(invoice.sub_total), 0) : 0;

  // Identify the payment stage
  useEffect(() => {
    if (receipts && receipts.length > 0 && !hasDebt) {
      setStep(PaymentSteps.Paid);
    } else if (
      (job?.status === 'open' && job?.products?.length > 0) ||
      (job?.status === 'cancelled' && Number(job?.sub_total) > 0 && invoiceSubtotal < Number(job?.sub_total))
    ) {
      setStep(PaymentSteps.GenerateInvoice);
    } else if (invoices && invoices.length > 0) {
      setStep(PaymentSteps.ProcessPayment);
    }
  }, [invoices, receipts]);

  // Flags and Calculations
  const receiptsTotal = receipts?.reduce((sum, item) => sum.plus(Big(item.amount)), Big(0)).toFixed(2) || '0.00';
  const invoicesTotal = invoices?.reduce((sum, item) => sum.plus(Big(item.total)), Big(0)).toFixed(2) || '0.00';
  const outstandingAmount = Big(invoicesTotal).minus(receiptsTotal).toFixed(2);
  const hasDebt = Big(outstandingAmount).gt(0);

  // to show skeleton
  const isDataLoading =
    isPaymentMethodsLoading ||
    isServiceLoading ||
    isInvoicesLoading ||
    isPaymentInfoLoading ||
    isReceiptsLoading ||
    isPaymentProcessing;

  // just disable the action buttons
  const isJobUpdating =
    isInvoiceCreating ||
    isServiceUpdating ||
    sendingEmail ||
    isRequestSending ||
    isReceiptsLoading ||
    isPaymentProcessing;

  return {
    PaymentSteps,
    jobDetails,
    step,
    invoices,
    receipts,
    updateThirdParty,
    paymentMethods,
    paymentMethod,
    setPaymentMethod,
    generateInvoice,
    requestPayment,
    processPayment,
    sendEmail,
    isPaymentRequested,
    initialContactValues,
    emailOptions,
    smsOptions,
    hasCard,
    hasDebt,
    receiptsTotal,
    outstandingAmount,
    isDataLoading,
    isJobUpdating,
    incompleteDetailsMessage,
  };
};
